import { queryOptions } from '@tanstack/react-query'
import type { DomainRow } from '../overview/columns'
import { apiGet } from './client'

/**
 * Contractul capetelor Overview pe care le cere DataTables / Today / Ahead /
 * ITIL / NIS2. Pagina HTML sta pe aceleasi cereri.
 */

/** Cele cinci pagini Overview mutate din CodeIgniter. */
export const PAGES = ['today', 'ahead', 'dashboard', 'nis2', 'domains'] as const
export type OverviewPage = (typeof PAGES)[number]

export function isOverviewPage(value: string): value is OverviewPage {
  return (PAGES as readonly string[]).includes(value)
}

export interface DomainTableRequest {
  draw: number
  start: number
  length: number
  search: string
  due: '' | 'expired' | 'soon' | 'ok' | 'none'
  company: number
}

export interface DomainTablePage {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: DomainRow[]
  ready: boolean
  registry?: DomainRegistry
  /** Clasa CMDB a registrului - al doilea segment din `cmdb/form/0/<clasa>`. */
  class?: { id: number; name?: string } | null
  /** `REC_CAN_EDIT` - butonul "Add domain", hotarat pe server (inventoree.cmdb.edit). */
  canEdit?: boolean
}

export interface TodayCounts {
  status: 'OK'
  counts: {
    alarms: number | null
    tickets: number | null
    changes: number | null
    silent: number | null
    discovered: number | null
    total: number
  }
}

export interface TodayRow {
  key: string
  label: string
  about: string
  url: string
  icon: string
  tone: string
  count: number | null
}

export interface AheadDisk {
  device_id: number
  name: string
  host?: string
  mount: string
  hours: number
  when: string
  rate: string
  free: number
  total: number
  used_pct: number | null
  url?: string
}

export interface AheadCapacity {
  device_id: number
  name: string
  days: number
  when: string
  now_pct: number
  per_day: number | string
  points: number
}

export interface AheadBattery {
  device_id: number
  name: string
  health: number
  cycles: number | null
  condition: string
}

export interface AheadRestart {
  device_id: number
  name: string
  tip: number
  since: string
  days: number
}

export interface AheadCert {
  device_id: number
  name: string
  subject: string
  issuer: string
  sources: string
  not_after: string
  days: number
}

export interface AheadPage {
  filling: AheadDisk[] | null
  memory: AheadCapacity[] | null
  cpu: AheadCapacity[] | null
  batteries: AheadBattery[] | null
  restarts: AheadRestart[] | null
  certificates: AheadCert[] | null
}

export interface ItilTileBlock {
  ready?: boolean
  [key: string]: unknown
}

export interface ItilPage {
  days: number
  map: Record<string, { labels: string[]; source: string }>
  changes: ItilTileBlock
  incidents: ItilTileBlock
  problems: ItilTileBlock
  requests: ItilTileBlock
}

export interface Nis2Evidence {
  label: string
  value: string
  bad?: boolean
}

export interface Nis2Letter {
  name: string
  asks: string
  state: string
  score: number | null
  link: string | null
  link_label: string | null
  evidence: Nis2Evidence[]
}

export interface Nis2Page {
  letters: Record<string, Nis2Letter>
  overall: number | null
  measured: number
  letter_count: number
  manual: number
  fleet_seen: number
  days: number
}

export interface DomainRegistry {
  title: string
  addLabel: string
  searchHint: string
  due: string
  dueLabel: string
  blurb: string
  showServices: boolean
  columns: { key: string; label: string; type: string }[]
}

export interface DomainsPage {
  registry: DomainRegistry
  class: { ID?: number; id?: number; code?: string; name?: string } | null
  cmdbUrl: string
}

export interface DomainsTable {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: Record<string, unknown>[]
}

export function domainTableParams(r: DomainTableRequest): URLSearchParams {
  const p = new URLSearchParams()
  p.set('draw', String(r.draw))
  p.set('start', String(r.start))
  p.set('length', String(r.length))
  p.set('search[value]', r.search)
  if (r.search) p.set('search_term', r.search)
  if (r.due) p.set('due', r.due)
  if (r.company > 0) p.set('company', String(r.company))
  return p
}

export function domainTableQuery(r: DomainTableRequest) {
  return queryOptions({
    queryKey: ['overview', 'domains', r],
    queryFn: () => apiGet<DomainTablePage>(`/overview/domains/table?${domainTableParams(r).toString()}`),
  })
}

/**
 * Insigna Today din antet - Overview::todayCounts, capatul
 * `/api/v2/overview/today/counts` (OverviewAjaxController), care da
 * `{status, counts}`. Se cheama, ca orice alta lista de optiuni de aici.
 */
export function todayCountsQuery() {
  return queryOptions({
    queryKey: ['overview', 'today-counts'],
    queryFn: () => apiGet<TodayCounts>('/overview/today/counts'),
  })
}

export const todayRowsQuery = queryOptions({
  queryKey: ['overview', 'today', 'rows'],
  queryFn: () => apiGet<{ rows: TodayRow[] }>('/overview/today'),
})

export const aheadQuery = queryOptions({
  queryKey: ['overview', 'ahead'],
  queryFn: () => apiGet<AheadPage>('/overview/ahead'),
})

export function itilQuery(days: 7 | 30 | 90) {
  return queryOptions({
    queryKey: ['overview', 'itil', days],
    queryFn: () => apiGet<Record<string, unknown>>(`/overview/itil?days=${days}`),
  })
}

export const nis2Query = queryOptions({
  queryKey: ['overview', 'nis2'],
  queryFn: () => apiGet<Record<string, unknown>>('/overview/nis2'),
})

/** O dovada a unei cerinte DNSC: un control al auditului, masurat. */
export interface DnscEvidence {
  key: string
  title: string
  verdict: 'pass' | 'fail' | 'not known' | 'observed'
  figure: string
  because: string
  note: string
  score: number | null
  exceptions: number | null
  why: string
  sample: string[]
  /** Intrebarea controlului, in engleza (`Controls::registry`). */
  question?: string
  /** Felul regulii: coverage (x din y bune), zero (niciuna), max, min, '' (pragul cardului). */
  rule?: 'coverage' | 'zero' | 'max' | 'min' | ''
  /** Cifra goala si numitorul ei, cand exista. */
  value?: number | null
  total?: number | null
}

/** Scorul de implementare propus pe o cerinta DNSC (`Audit\Dnsc::propose`). */
export interface DnscRequirement {
  id: string
  level: 'B' | 'I' | 'E'
  key: boolean
  text: string
  score: number | null
  complete: boolean
  still: string
  evidence: DnscEvidence[]
}

export interface DnscPage {
  ready: boolean
  ceiling?: number
  requirements?: DnscRequirement[]
}

/** GET /api/v2/overview/nis2/dnsc - separat de litere, ca ele sa nu astepte. */
export const nis2DnscQuery = queryOptions({
  queryKey: ['overview', 'nis2', 'dnsc'],
  queryFn: () => apiGet<DnscPage>('/overview/nis2/dnsc'),
})
